/**
 * Shared Redux store configuration
 *
 * This file exports the shared slices and a base store configuration.
 * The configuratorStore can be used directly or extended by consuming apps.
 */

import { configureStore, Reducer, Middleware } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

import { accordionSlice } from '../features/accordion/accordionSlice';
import { apiSlice } from '../features/api/store/apiSlice';
import { itemSlice } from '../features/item/store/itemSlice';

// Export slices for apps that want to extend the store
export { accordionSlice, apiSlice, itemSlice };

// Core reducers used by the shared components
export const coreReducers = {
  accordion: accordionSlice.reducer,
  item: itemSlice.reducer,
  [apiSlice.reducerPath]: apiSlice.reducer
};

// Core middleware
export const coreMiddleware = [apiSlice.middleware];

/**
 * Create a configurator store with optional additional reducers and middleware.
 * This allows consuming apps to extend the store with their own slices.
 */
export function createConfiguratorStore(options?: {
  additionalReducers?: Record<string, Reducer>;
  additionalMiddleware?: Middleware[];
}) {
  const { additionalReducers = {}, additionalMiddleware = [] } = options ?? {};

  return configureStore({
    reducer: {
      ...coreReducers,
      ...additionalReducers
    },
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat(...coreMiddleware, ...additionalMiddleware)
  });
}

// Default store instance with just core reducers
export const configuratorStore = createConfiguratorStore();

// Type definitions
export type ConfiguratorStore = typeof configuratorStore;
export type RootState = ReturnType<typeof configuratorStore.getState>;
export type ConfiguratorDispatch = typeof configuratorStore.dispatch;

// Typed hooks for use in components
export const useConfiguratorDispatch: () => ConfiguratorDispatch = useDispatch;
export const useRootSelector: TypedUseSelectorHook<RootState> = useSelector;

// Re-export Redux utilities
export { Provider } from 'react-redux';
export { configureStore } from '@reduxjs/toolkit';
export type { TypedUseSelectorHook } from 'react-redux';
